/**
 * Industries. Verbatim from design/Homepage.dc.html lines 275-345.
 *
 * The `tags` are the frameworks WeAreQA tests against, not certifications the
 * company holds. Rendered as plain outlined pills, never as badges.
 */

export const industriesSection = {
  eyebrow: "Industries",
  heading: "A test is only as good as the domain logic behind it.",
} as const;

export type Industry = {
  number: string;
  name: string;
  tabDetail: string;
  body: string;
  checks: string[];
  tags: string[];
  link: { label: string; href: string };
};

/**
 * Held at "#" in the reference build because the industry pages did not exist
 * yet. They do now, so each panel points at its own.
 *
 * The Healthcare & Pharma panel names two industries that are two separate
 * pages here. It goes to healthcare, not the hub: its body is SaMD validation,
 * digital health and AI diagnostics, which is that page's subject — Pharma's is
 * computer system validation, a different one. Pharma is in the Industries menu.
 */
const LINK = (href: string) => ({ label: "Explore this industry", href });

export const industries: Industry[] = [
  {
    number: "01",
    name: "FinTech & Financial Services",
    tabDetail: "Payments, transaction security, risk-scoring agents",
    body: "Payment gateway and transaction-security testing, API integration checks, and validation of automated risk-scoring and financial decision agents.",
    checks: [
      "Payment gateway and settlement flows",
      "Transaction security and fraud paths",
      "Automated risk-scoring agent outputs",
      "Third-party API integration contracts",
    ],
    tags: ["PCI-DSS", "SOC 2", "GDPR"],
    link: LINK("/industries/fintech"),
  },
  {
    number: "02",
    name: "Healthcare & Pharma",
    tabDetail: "SaMD validation, digital health, AI diagnostics",
    body: "Software as a Medical Device validation, digital health application testing, and output verification for AI diagnostic support systems.",
    checks: [
      "SaMD validation and documentation trails",
      "Patient data handling and access control",
      "AI diagnostic support output verification",
      "Interoperability across clinical systems",
    ],
    tags: ["HIPAA", "FDA guidance", "ISO 13485"],
    link: LINK("/industries/healthcare"),
  },
  {
    number: "03",
    name: "eCommerce & Retail",
    tabDetail: "Checkout integrity, peak load, legacy modernization",
    body: "Checkout and catalogue integrity, peak-load performance, and legacy marketplace modernization without breaking what already sells.",
    checks: [
      "Checkout, payment and refund journeys",
      "Catalogue and inventory integrity",
      "Peak-load and Black Friday readiness",
      "Legacy marketplace migration safety",
    ],
    tags: ["Peak load", "Payments", "Legacy"],
    link: LINK("/industries/ecommerce"),
  },
];
